import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PaperPlaneRight, Paperclip, FileArrowDown } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmt = (iso) => new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });

function Attachment({ att }) {
  const [url, setUrl] = useState(null);
  const isImage = (att.content_type || "").startsWith("image/");
  useEffect(() => {
    let revoke;
    if (isImage) {
      api.get(`/files/${att.file_id}`, { responseType: "blob" })
        .then((r) => { const u = URL.createObjectURL(r.data); revoke = u; setUrl(u); })
        .catch(() => {});
    }
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [att.file_id, isImage]);

  const open = async () => {
    try {
      const r = await api.get(`/files/${att.file_id}`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      window.open(u, "_blank");
    } catch { toast.error("Could not open file"); }
  };

  if (isImage) {
    return url
      ? <img src={url} alt={att.filename} onClick={open} className="mt-1 max-h-48 rounded cursor-pointer border border-black/10" data-testid="chat-attachment-image" />
      : <div className="mt-1 text-xs opacity-70">Loading image…</div>;
  }
  return (
    <button onClick={open} data-testid="chat-attachment-file" className="mt-1 flex items-center gap-2 text-xs underline underline-offset-2">
      <FileArrowDown size={14} weight="bold" /> {att.filename}
    </button>
  );
}

export default function Chat() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const firstLoad = useRef(true);

  const load = () => api.get("/chat/messages").then((r) => setMessages(r.data || [])).catch(() => {});

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: firstLoad.current ? "auto" : "smooth" });
    firstLoad.current = false;
    if (messages.length) {
      localStorage.setItem("chat_last_read", messages[messages.length - 1].created_at);
      window.dispatchEvent(new Event("chat-read"));
    }
  }, [messages]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true); setText("");
    try { await api.post("/chat/messages", { text: t }); await load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); setText(t); }
    finally { setSending(false); }
  };

  const onAttach = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/chat/messages", { text: text.trim(), file_id: up.data.id });
      setText("");
      await load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  return (
    <>
      <PageHeader title="Team Chat" subtitle="Shared Channel" />
      <div className="flex flex-col" style={{ height: "calc(100vh - 220px)" }}>
        <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-4 space-y-3" data-testid="chat-messages">
          {messages.length === 0 && <p className="text-sm text-zinc-400 text-center mt-8">No messages yet. Say hello 👋</p>}
          {messages.map((m) => {
            const mine = m.user_id === user?.id;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
                <div className={`max-w-[80%] sm:max-w-[60%] px-3 py-2 ${mine ? "bg-primary text-white" : "bg-white border border-zinc-300 text-zinc-900"}`}>
                  {!mine && <p className="text-[11px] font-bold uppercase tracking-wide text-zinc-500 mb-0.5">{m.user_name}</p>}
                  {m.text && <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>}
                  {m.attachment && <Attachment att={m.attachment} />}
                  <p className={`text-[10px] mt-1 ${mine ? "text-orange-100" : "text-zinc-400"}`}>{fmt(m.created_at)}</p>
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>
        <div className="border-t border-zinc-300 bg-white px-4 sm:px-8 py-3 flex items-center gap-2">
          <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={onAttach} className="hidden" data-testid="chat-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={sending} variant="outline" data-testid="chat-attach-button" className="rounded-none border-zinc-300 shrink-0">
            <Paperclip size={16} weight="bold" />
          </Button>
          <Input
            data-testid="chat-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Type a message…"
            className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary"
          />
          <Button onClick={send} disabled={sending} data-testid="chat-send-button" className="rounded-none bg-primary hover:bg-[#C2410C] text-white shrink-0">
            <PaperPlaneRight size={16} weight="bold" />
          </Button>
        </div>
      </div>
    </>
  );
}
